import { useAuthStore } from '@/stores/auth.store';

export type FrontendEventType =
  | 'page_view'
  | 'user_action'
  | 'api_error'
  | 'js_error'
  | 'auth_event'
  | 'fbr_event'
  | 'sale_event'
  | 'ws_event'
  | 'perf_metric';

interface LogEvent {
  eventType: FrontendEventType;
  page?: string;
  action?: string;
  message?: string;
  durationMs?: number;
  metadata?: Record<string, unknown>;
}

const BATCH_INTERVAL_MS = 5_000;
const MAX_BATCH_SIZE = 20;

class FrontendLogger {
  private queue: LogEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private sending = false;

  /** Queue an event. The batch is flushed every 5 s or when it hits 20 items. */
  track(event: LogEvent): void {
    this.queue.push(event);
    if (this.queue.length >= MAX_BATCH_SIZE) {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => this.flush(), BATCH_INTERVAL_MS);
    }
  }

  /** Flush the queue to the backend log ingestion endpoint. */
  private async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.queue.length === 0 || this.sending) return;

    const batch = this.queue.splice(0, MAX_BATCH_SIZE);
    this.sending = true;

    const { accessToken, tenant } = useAuthStore.getState();
    if (!accessToken) {
      // Not authenticated — discard events silently
      this.sending = false;
      return;
    }

    try {
      await Promise.all(
        batch.map((event) =>
          fetch('/api/v1/logs/frontend', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
              ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
            },
            body: JSON.stringify({
              ...event,
              tenant: tenant?.slug,
            }),
          }).catch(() => {
            // Silently ignore — log shipping should never break the UI
          }),
        ),
      );
    } finally {
      this.sending = false;
      // If new events arrived while we were sending, schedule another flush
      if (this.queue.length > 0) {
        this.timer = setTimeout(() => this.flush(), BATCH_INTERVAL_MS);
      }
    }
  }

  // ─── Convenience helpers ────────────────────────────────────────────────────

  pageView(page: string, metadata?: Record<string, unknown>): void {
    this.track({ eventType: 'page_view', page, metadata });
  }

  userAction(action: string, page?: string, metadata?: Record<string, unknown>): void {
    this.track({ eventType: 'user_action', action, page, metadata });
  }

  apiError(url: string, status: number, page?: string, metadata?: Record<string, unknown>): void {
    this.track({
      eventType: 'api_error',
      page,
      action: url,
      message: `HTTP ${status} on ${url}`,
      metadata: { status, ...metadata },
    });
  }

  jsError(error: Error, page?: string): void {
    this.track({
      eventType: 'js_error',
      page,
      message: error.message,
      metadata: { stack: error.stack?.substring(0, 500) },
    });
  }

  authEvent(action: string, metadata?: Record<string, unknown>): void {
    this.track({ eventType: 'auth_event', action, metadata });
  }

  fbrEvent(action: string, metadata?: Record<string, unknown>): void {
    this.track({ eventType: 'fbr_event', action, metadata });
  }

  saleEvent(action: string, metadata?: Record<string, unknown>): void {
    this.track({ eventType: 'sale_event', action, metadata });
  }

  perfMetric(name: string, durationMs: number, page?: string): void {
    this.track({ eventType: 'perf_metric', action: name, durationMs, page });
  }

  wsEvent(event: string, page?: string, metadata?: Record<string, unknown>): void {
    this.track({ eventType: 'ws_event', action: event, page, metadata });
  }
}

/** Singleton logger — import and use anywhere in the frontend. */
export const logger = new FrontendLogger();
